import { StatusBadge } from '@components/Shared/DataTable/TableCells';
import {
  dialogFrame, dialogLine, dialogTray, formDialogBodyStyles, formDialogCancelStyles, formDialogCloseStyles,
  formDialogDangerStyles, formDialogFieldStyles, formDialogFooterStyles, formDialogHeaderStyles, formDialogLabelStyles,
  formDialogMarkStyles, formDialogPaperStyles, formDialogSubmitStyles, formDialogTextStyles, formDialogTitleStyles
} from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import {
  Autocomplete, Box, Button, Checkbox, createFilterOptions, Dialog, FormLabel, IconButton, Skeleton, TextField, Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type TeamRole } from '@store/api/teamEndpoints';
import {
  type TransferMembership, type User, useDeleteUserMutation, useGetUsersQuery, useGetUserTransferPreviewQuery,
  type UserTransferChoice, type UserTransferPreview
} from '@store/api/userEndpoints';
import { useAppDispatch } from '@store/hooks';
import { showSnackbar } from '@store/reducers/snackbarSlice';
import { brand, gray, orange, red } from '@theme/primitives';
import {
  ArrowRightIcon, ArrowRightLeftIcon, ClipboardCheckIcon, InfoIcon, ShieldCheckIcon, Trash2Icon, TriangleAlertIcon,
  UsersIcon, XIcon
} from 'lucide-react';
import { type MouseEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import UserAvatar from './UserAvatar';

interface DeleteUserDialogProps {
  open: boolean;
  /**
   * The account to delete. The caller mounts the dialog anew for every opening (by `key`), so
   * nothing chosen the last time is carried over, and keeps it mounted until `onExited`.
   */
  user: User;
  onClose: () => void;
  /** The dialog has faded out: the caller lets it go, and its queries end with it. */
  onExited: () => void;
}

type Choice = Omit<UserTransferChoice, 'to'>;

/** A team row of the teams card: the membership, and once an account is picked, what it holds there. */
type TeamRow = TransferMembership & { current?: TeamRole | null; result?: TeamRole };

/** What picking somebody to take over starts from: the work, never the role, which is a grant of its own. */
const pickedChoice: Choice = { inspections: true, teams: true, role: false };

const teamRoleKey = (role: TeamRole) => `Team_Role_${role.toLowerCase().capitalize()}`;

// A person is found by their username as well as their name.
const filterPeople = createFilterOptions<User>({ stringify: (option) => `${option.name} ${option.username}` });

/** An account named so that two of the same name are told apart: only usernames are unique. */
const accountName = (person: Pick<User, 'name' | 'username'>) => `${person.name} (@${person.username})`;

/**
 * How long after the second step appears a press is still taken for the end of the press that
 * showed it: the second click of a double-click on Continue, which lands where Continue stood, on
 * the delete button or, where the second step is shorter, beside the dialog. Longer than a
 * double-click, shorter than reading the step.
 */
const settleMs = 500;

/**
 * Deleting one account from the Users page, with what it holds handed to somebody else first.
 *
 * The first step shows what the account holds (its inspections, its teams with its role in each,
 * an admin's role) and lets whoever deletes it pick an account to take any of it over: each card
 * says what would happen, read from the server as the two accounts stand
 * (`GET /v1/users/:id/transfer-preview`). In the teams the account taking over joins at the
 * deleted account's role where it is not a member, keeps its own role where it is, and becomes
 * MANAGER where the deleted account was one: a merge raises a role only to MANAGER. What single
 * sign-on decides for the account taking over is locked, and its card says only that. The second
 * step says once more what is handed over and what is left behind before the delete, which cannot
 * be taken back, and only a press of its own button deletes. An account that holds nothing to
 * hand over is asked about at once.
 */
const DeleteUserDialog = ({ open, user, onClose, onExited }: DeleteUserDialogProps) => {
  const { t, currentLanguage } = useLocalization();
  const dispatch = useAppDispatch();
  const titleId = useId();
  const pickerId = useId();
  const confirmationRef = useRef<HTMLDivElement>(null);
  // When the second step appeared, by the clock events carry; `null` while it never has.
  const confirmShownAt = useRef<number | null>(null);

  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [recipient, setRecipient] = useState<User | null>(null);
  const [choice, setChoice] = useState<Choice>(pickedChoice);
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  // Read for as long as the dialog is mounted, while it fades out too: a query skipped on closing
  // has no data, and the second step turned into the plain delete's as the dialog faded.
  const holdingsQuery = useGetUserTransferPreviewQuery({ id: user.id }, { refetchOnMountOrArgChange: true });
  const planQuery = useGetUserTransferPreviewQuery(
    { id: user.id, to: recipient?.id },
    { skip: !recipient, refetchOnMountOrArgChange: true }
  );
  const directoryQuery = useGetUsersQuery();

  const holdings = holdingsQuery.data;
  // The plan of the account picked now, never the one picked before it while the next is read,
  // nor one being read again after a refused delete, nor one whose reading again failed.
  const plan = recipient && planQuery.isSuccess && !planQuery.isFetching ? planQuery.currentData : undefined;
  const directory = directoryQuery.data;
  const holdingsFailed = holdingsQuery.isError && !holdings;
  const directoryFailed = directoryQuery.isError && !directory;

  const inspections = holdings?.inspections ?? 0;
  const memberships = holdings?.memberships ?? [];
  const isAdmin = (holdings?.from.role ?? user.role) === 'admin';
  // Read and found empty: the dialog asks about the delete at once.
  const holdsNothing = holdings !== undefined && inspections === 0 && memberships.length === 0 && !isAdmin;

  const people = useMemo(() => (directory ?? [])
    .filter((person) => person.id !== user.id)
    .sort((a, b) => a.name.localeCompare(b.name, currentLanguage)), [directory, user.id, currentLanguage]);

  const teamChanges = plan?.teams?.merge.filter((team) => team.result !== team.current) ?? [];
  const teamRows: TeamRow[] = plan?.teams?.merge ?? memberships;
  const available: Choice = {
    inspections: plan !== undefined && inspections > 0,
    teams: Boolean(plan?.teams?.transferable) && teamChanges.length > 0,
    role: Boolean(plan?.role?.offered && plan.role.transferable),
  };
  // What is handed over is what is both chosen and possible: a card switched off by what the plan
  // says keeps the reader's choice, so picking another account brings it back.
  const chosen: Choice = {
    inspections: choice.inspections && available.inspections,
    teams: choice.teams && available.teams,
    role: choice.role && available.role,
  };
  const handsOver = recipient !== null && (chosen.inspections || chosen.teams || chosen.role);

  const pick = (person: User | null) => {
    setRecipient(person);
    setChoice(pickedChoice);
  };

  const remove = async () => {
    const transfer = handsOver && recipient ? { to: recipient.id, ...chosen } : undefined;
    const result = await deleteUser({ id: user.id, transfer, showSuccess: !transfer });

    // A refusal is said by the request's own snackbar. What refused it may also have changed what
    // can be handed over (single sign-on taking over the account picked, say), and the plan has no
    // tag to be refreshed by: both accounts are read again, and the cards show what still can be,
    // with the same account picked.
    if (result.error) {
      setStep('choose');
      void holdingsQuery.refetch();
      void directoryQuery.refetch();
      if (recipient) void planQuery.refetch();
      return;
    }

    if (transfer && recipient) {
      const message = t('User_Delete_Done_Transfer', [accountName(user), accountName(recipient)]);

      dispatch(showSnackbar({ severity: 'success', message }));
    }
    onClose();
  };

  const proceed = (event: MouseEvent) => {
    confirmShownAt.current = event.timeStamp;
    setStep('confirm');
  };

  // A later click of a burst, or one as soon as the second step appeared: the rest of a
  // double-click on Continue, not a press of its own.
  const isAfterpress = (event: { detail: number; timeStamp: number }) => {
    const shownAt = confirmShownAt.current;

    return event.detail > 1 || (shownAt !== null && event.timeStamp - shownAt < settleMs);
  };

  // Only a press of this button's own deletes.
  const confirmRemove = (event: MouseEvent) => {
    if (isAfterpress(event)) return;
    void remove();
  };

  // Nor does such a click close the dialog, landing beside a second step shorter than the first,
  // where the second step would never have been read.
  const closeAsked = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && isAfterpress(event as MouseEvent)) return;
    onClose();
  };

  // The second step takes the keyboard's focus, so that a second Enter or Space meant for
  // Continue falls on what the step says, not on the button that now stands where Continue stood.
  useEffect(() => {
    if (step === 'confirm') confirmationRef.current?.focus();
  }, [step]);

  const asking = step === 'confirm' || holdsNothing;

  return (
    <Dialog
      open={open}
      onClose={isDeleting ? undefined : closeAsked}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: paperStyles }, transition: { onExited } }}
    >
      <Box sx={formDialogHeaderStyles}>
        <Box sx={formDialogMarkStyles(red[300])}><Trash2Icon size={16} /></Box>
        <Typography id={titleId} component="h2" sx={formDialogTitleStyles}>{t('User_Delete_Title')}</Typography>
        <IconButton aria-label={t('Common_Close')} onClick={onClose} disabled={isDeleting} sx={formDialogCloseStyles}>
          <XIcon size={18} />
        </IconButton>
      </Box>

      <Box sx={bodyStyles}>
        <AccountCard user={user} holdings={holdings} isLoading={holdingsQuery.isFetching && !holdings} />

        {/* The account, or the accounts the picker offers: read again, each, only where it failed. */}
        {(holdingsFailed || directoryFailed) && (
          <LoadFailed
            onRetry={() => {
              if (holdingsFailed) void holdingsQuery.refetch();
              if (directoryFailed) void directoryQuery.refetch();
            }}
            disabled={holdingsQuery.isFetching || directoryQuery.isFetching}
          />
        )}

        {asking ? (
          <Box ref={confirmationRef} tabIndex={-1} sx={confirmationStyles}>
            <Confirmation
              user={user}
              recipient={handsOver ? recipient : null}
              plan={plan}
              chosen={chosen}
              inspections={inspections}
              memberships={memberships.length}
              isAdmin={isAdmin}
              holdsNothing={holdsNothing}
            />
          </Box>
        ) : holdings && (
          <>
            <Box>
              <FormLabel htmlFor={pickerId} sx={formDialogLabelStyles}>{t('User_Delete_HandOver_Label')}</FormLabel>
              <Autocomplete<User>
                id={pickerId}
                options={people}
                value={recipient}
                onChange={(_event, person) => pick(person)}
                loading={directoryQuery.isFetching && !directory}
                filterOptions={filterPeople}
                getOptionKey={(option) => option.id}
                getOptionLabel={accountName}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // A list that could not be read is not a list of nobody.
                noOptionsText={t(directoryFailed ? 'User_Delete_LoadFailed' : 'User_Delete_HandOver_NoOptions')}
                renderOption={(props, option) => {
                  const { key, ...optionProps } = props;

                  return (
                    <Box component="li" key={key} {...optionProps} sx={optionStyles}>
                      <UserAvatar name={option.name} size={28} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: 14, fontWeight: 500 }}>{option.name}</Typography>
                        <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary' }}>@{option.username}</Typography>
                      </Box>
                      {option.isActive === false && <StatusBadge label={t('User_Suspended')} tone="danger" />}
                      {option.role === 'admin' && <StatusBadge label={t('User_Role_Admin')} tone="info" />}
                    </Box>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t('User_Delete_HandOver_Placeholder')}
                    sx={formDialogFieldStyles}
                  />
                )}
              />
              <Typography sx={hintStyles}>{t('User_Delete_HandOver_Hint')}</Typography>
            </Box>

            <Box>
              <Typography component="h3" sx={sectionTitleStyles}>{t('User_Delete_What')}</Typography>

              {recipient && planQuery.isError && !plan && (
                <LoadFailed onRetry={() => void planQuery.refetch()} disabled={planQuery.isFetching} />
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <ChoiceCard
                  icon={<ClipboardCheckIcon size={16} />}
                  title={t('Common_Inspections')}
                  count={inspections}
                  checked={chosen.inspections}
                  disabled={!available.inspections}
                  onChange={(checked) => setChoice({ ...choice, inspections: checked })}
                  note={inspections === 0
                    ? t('User_Delete_Inspections_None')
                    : recipient ? t('User_Delete_Inspections_Desc', [recipient.name]) : t('User_Delete_PickFirst')}
                />

                <ChoiceCard
                  icon={<UsersIcon size={16} />}
                  title={t('Common_Teams')}
                  count={memberships.length}
                  checked={chosen.teams}
                  disabled={!available.teams}
                  onChange={(checked) => setChoice({ ...choice, teams: checked })}
                  note={teamsNote(memberships.length, recipient, plan, teamChanges.length, t)}
                  warning={recipient && plan?.teams && !plan.teams.transferable
                    ? t('User_Delete_Teams_Sso', [recipient.name])
                    : undefined}
                >
                  {memberships.length > 0 && (
                    <Box component="ul" sx={teamListStyles}>
                      {teamRows.map((team) => (
                        <Box component="li" key={team.teamId} sx={teamRowStyles}>
                          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: 13 }}>{team.teamName}</Typography>
                          <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t(teamRoleKey(team.role))}</Typography>
                          {/* With somebody picked: what they hold there afterwards, once the plan is in, and
                              nothing where single sign-on keeps their teams, since none of it would be written. */}
                          {recipient && plan?.teams?.transferable !== false && (
                            team.result !== undefined
                              ? <TeamOutcome current={team.current ?? null} result={team.result} />
                              : planQuery.isFetching && <Skeleton variant="text" width={96} sx={{ fontSize: 12.5 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </ChoiceCard>

                {isAdmin && (
                  <ChoiceCard
                    icon={<ShieldCheckIcon size={16} />}
                    title={t('User_Delete_Role_Title')}
                    checked={chosen.role}
                    disabled={!available.role}
                    onChange={(checked) => setChoice({ ...choice, role: checked })}
                    // Where single sign-on keeps the role, its warning is all the card says.
                    note={!recipient ? t('User_Delete_PickFirst')
                      : !plan ? undefined
                        : !plan.role?.offered ? t('User_Delete_Role_Already', [recipient.name])
                          : plan.role.transferable ? t('User_Delete_Role_Desc', [recipient.name])
                            : undefined}
                    warning={recipient && plan?.role?.offered && !plan.role.transferable
                      ? t('User_Delete_Role_Sso', [recipient.name])
                      : undefined}
                  />
                )}
              </Box>
            </Box>
          </>
        )}
      </Box>

      <Box sx={formDialogFooterStyles}>
        {step === 'confirm' ? (
          <Button onClick={() => setStep('choose')} disabled={isDeleting} sx={formDialogCancelStyles}>
            {t('User_Delete_Back')}
          </Button>
        ) : (
          <Button onClick={onClose} disabled={isDeleting} sx={formDialogCancelStyles}>
            {t('Common_Cancel')}
          </Button>
        )}

        {/* Keyed apart, so that React makes the delete button a button of its own and does not
            hand it Continue's, focus and all, for the second press of Enter or Space. */}
        {asking ? (
          <Button key="delete" onClick={confirmRemove} loading={isDeleting} sx={formDialogDangerStyles}>
            {t(handsOver ? 'User_Delete_SubmitTransfer' : 'User_Delete_Submit')}
          </Button>
        ) : (
          // Not before the account was read, unless reading it failed: the delete is still there to
          // be made, with nothing handed over. Nor while it is read again, nor with somebody picked
          // whose plan is not in yet: whoever picked them meant to hand something over, and would
          // be asked about a delete that hands over nothing.
          <Button
            key="continue"
            onClick={proceed}
            disabled={(!holdings && !holdingsQuery.isError) || holdingsQuery.isFetching || (recipient !== null && !plan)}
            sx={formDialogSubmitStyles}
          >
            {t('User_Delete_Continue')}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

/** What the teams card says under its title, as far as what is known allows. */
const teamsNote = (
  count: number,
  recipient: User | null,
  plan: UserTransferPreview | undefined,
  changes: number,
  t: (key: string, values?: string[]) => string
): string | undefined => {
  if (count === 0) return t('User_Delete_Teams_None');
  if (!recipient) return t('User_Delete_PickFirst');
  if (!plan?.teams) return undefined;
  if (!plan.teams.transferable) return undefined;

  return changes === 0 ? t('User_Delete_Teams_NoChange', [recipient.name]) : t('User_Delete_Teams_Desc', [recipient.name]);
};

/** Who is deleted, and what they hold, at a glance. */
const AccountCard = ({ user, holdings, isLoading }: {
  user: User;
  holdings: UserTransferPreview | undefined;
  isLoading: boolean;
}) => {
  const { t } = useLocalization();
  const role = holdings?.from.role ?? user.role;

  return (
    <Box sx={accountCardStyles}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <UserAvatar name={user.name} size={40} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 15, fontWeight: 600, lineHeight: '22px' }}>{user.name}</Typography>
          <Typography noWrap sx={{ fontSize: 13, color: 'text.secondary' }}>
            @{user.username}{user.email ? ` · ${user.email}` : ''}
          </Typography>
        </Box>
        {user.isActive === false && <StatusBadge label={t('User_Suspended')} tone="danger" />}
      </Box>

      <Box sx={statsStyles}>
        <Stat icon={<ClipboardCheckIcon size={14} />} label={t('Common_Inspections')} value={holdings?.inspections} isLoading={isLoading} />
        <Stat icon={<UsersIcon size={14} />} label={t('Common_Teams')} value={holdings?.memberships.length} isLoading={isLoading} />
        <Stat
          icon={<ShieldCheckIcon size={14} />}
          label={t('Common_Role')}
          value={t(`User_Role_${role.capitalize()}`)}
          tone={role === 'admin' ? brand[300] : undefined}
          isLoading={false}
        />
      </Box>
    </Box>
  );
};

const Stat = ({ icon, label, value, tone, isLoading }: {
  icon: ReactNode;
  label: string;
  value: number | string | undefined;
  tone?: string;
  isLoading: boolean;
}) => (
  <Box sx={statStyles}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
      {icon}
      <Typography noWrap sx={{ fontSize: 12, fontWeight: 500, color: 'inherit' }}>{label}</Typography>
    </Box>
    {isLoading
      ? <Skeleton variant="text" width={32} sx={{ fontSize: 18 }} />
      : <Typography noWrap sx={{ fontSize: 18, fontWeight: 600, lineHeight: '26px', color: tone ?? 'text.primary' }}>{value ?? '—'}</Typography>}
  </Box>
);

/**
 * One thing that can be handed over, as a card whose checkbox, title and note are its label: the
 * role cards of the user dialog, framed in the page's blue when chosen. What goes under them (the
 * teams) stands outside the label, so that a click there to read a team's name chooses nothing,
 * and the label holds only phrasing content.
 */
const ChoiceCard = ({ icon, title, count, checked, disabled, onChange, note, warning, children }: {
  icon: ReactNode;
  title: string;
  count?: number;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  note?: string;
  warning?: string;
  children?: ReactNode;
}) => {
  const noteId = useId();

  return (
    <Box sx={choiceCardStyles(checked, disabled)}>
      <Box component="label" sx={choiceLabelStyles(disabled)}>
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          inputProps={{ 'aria-label': title, 'aria-describedby': noteId }}
          sx={{ p: 0, mt: '1px' }}
        />
        <Box component="span" sx={choiceIconStyles(checked)}>{icon}</Box>
        <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
          <Box component="span" sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography component="span" sx={{ flex: 1, fontSize: 14, fontWeight: 500, lineHeight: '20px' }}>{title}</Typography>
            {count !== undefined && (
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                {count}
              </Typography>
            )}
          </Box>
          <Box component="span" id={noteId} sx={{ display: 'block' }}>
            {note && (
              <Typography component="span" sx={{ display: 'block', fontSize: 12.5, lineHeight: '18px', color: 'text.secondary', mt: 0.25 }}>
                {note}
              </Typography>
            )}
            {warning && (
              <Box component="span" sx={warningLineStyles}>
                <TriangleAlertIcon size={13} />
                <span>{warning}</span>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {children && <Box sx={{ gridColumn: 3, minWidth: 0 }}>{children}</Box>}
    </Box>
  );
};

/** What the account taking over holds in a team afterwards. */
const TeamOutcome = ({ current, result }: { current: TeamRole | null; result: TeamRole }) => {
  const { t } = useLocalization();

  if (current === null) {
    return <Typography noWrap sx={{ ...outcomeStyles, color: brand[300] }}>{t('User_Delete_Team_Joins', [t(teamRoleKey(result))])}</Typography>;
  }

  if (current === result) {
    return <Typography noWrap sx={{ ...outcomeStyles, color: 'text.disabled' }}>{t('User_Delete_Team_Already', [t(teamRoleKey(current))])}</Typography>;
  }

  return (
    <Box sx={{ ...outcomeStyles, display: 'flex', alignItems: 'center', gap: 0.5, color: brand[300] }}>
      <span>{t(teamRoleKey(current))}</span>
      <ArrowRightIcon size={12} aria-hidden />
      <span>{t(teamRoleKey(result))}</span>
    </Box>
  );
};

/** The second step: what goes to whom, or what is left behind, and that it is final. */
const Confirmation = ({ user, recipient, plan, chosen, inspections, memberships, isAdmin, holdsNothing }: {
  user: User;
  /** The account taking over, where anything is handed over at all. */
  recipient: User | null;
  plan: UserTransferPreview | undefined;
  chosen: Choice;
  inspections: number;
  memberships: number;
  isAdmin: boolean;
  holdsNothing: boolean;
}) => {
  const { t } = useLocalization();

  if (recipient) {
    const teamLines = chosen.teams
      ? (plan?.teams?.merge ?? []).filter((team) => team.result !== team.current).map((team) => (team.current === null
        ? t('User_Delete_Confirm_TeamJoins', [team.teamName, t(teamRoleKey(team.result))])
        : t('User_Delete_Confirm_TeamRaised', [team.teamName, t(teamRoleKey(team.result))])))
      : [];

    // What a partial hand-over leaves behind, said as the plain delete says it.
    const orphans = !chosen.inspections && inspections > 0;
    const leavesTeams = !chosen.teams && memberships > 0;

    return (
      <>
        <Panel tone="info" icon={<ArrowRightLeftIcon size={16} />} title={t('User_Delete_Confirm_TakesOver', [accountName(recipient)])}>
          <Box component="ul" sx={bulletListStyles}>
            {chosen.inspections && <li>{t('User_Delete_Confirm_Inspections', { count: inspections })}</li>}
            {teamLines.map((line) => <li key={line}>{line}</li>)}
            {chosen.role && <li>{t('User_Delete_Role_Title')}</li>}
          </Box>
        </Panel>
        <Panel tone="danger" icon={<TriangleAlertIcon size={16} />} title={t('User_Delete_Confirm_Then', [user.name])}>
          {(orphans || leavesTeams) && (
            <Box component="ul" sx={bulletListStyles}>
              {orphans && <li>{t('User_Delete_Confirm_Orphans', { count: inspections })}</li>}
              {leavesTeams && <li>{t('User_Delete_Confirm_LeavesTeams', { count: memberships })}</li>}
            </Box>
          )}
          <Typography sx={{ ...panelTextStyles, fontWeight: 500, color: 'text.primary' }}>{t('User_Delete_Confirm_Undone')}</Typography>
        </Panel>
      </>
    );
  }

  return (
    <Panel tone="danger" icon={<TriangleAlertIcon size={16} />} title={t('User_Delete_Confirm_Permanent', [user.name])}>
      {holdsNothing && <Typography sx={panelTextStyles}>{t('User_Delete_Nothing')}</Typography>}
      {!holdsNothing && (inspections > 0 || memberships > 0 || isAdmin) && (
        <Box component="ul" sx={bulletListStyles}>
          {inspections > 0 && <li>{t('User_Delete_Confirm_Orphans', { count: inspections })}</li>}
          {memberships > 0 && <li>{t('User_Delete_Confirm_LeavesTeams', { count: memberships })}</li>}
        </Box>
      )}
      <Typography sx={{ ...panelTextStyles, fontWeight: 500, color: 'text.primary' }}>{t('User_Delete_Confirm_Undone')}</Typography>
    </Panel>
  );
};

const panelTones = {
  info: { color: brand[300], line: alpha(brand[300], 0.28), fill: alpha(brand[300], 0.06) },
  danger: { color: red[300], line: alpha(red[300], 0.32), fill: alpha(red[300], 0.07) },
};

/** A statement the second step makes, in the colour of what it means. */
const Panel = ({ tone, icon, title, children }: {
  tone: keyof typeof panelTones;
  icon: ReactNode;
  title: string;
  children?: ReactNode;
}) => {
  const { color, line, fill } = panelTones[tone];

  return (
    <Box sx={{ display: 'flex', gap: 1.25, p: 1.5, border: '1px solid', borderColor: line, borderRadius: '10px', backgroundColor: fill }}>
      <Box sx={{ color, flexShrink: 0, mt: '2px' }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: '22px' }}>{title}</Typography>
        {children}
      </Box>
    </Box>
  );
};

const LoadFailed = ({ onRetry, disabled }: { onRetry: () => void; disabled: boolean }) => {
  const { t } = useLocalization();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: orange[300], mb: 1 }}>
      <InfoIcon size={14} />
      <Typography sx={{ flex: 1, fontSize: 13 }}>{t('User_Delete_LoadFailed')}</Typography>
      <Button size="small" onClick={onRetry} disabled={disabled} sx={{ textTransform: 'none', fontSize: 13 }}>
        {t('ReportPreview_Retry')}
      </Button>
    </Box>
  );
};

// Wider than a record dialog, for the cards and the teams in them; scrolls between its header and footer.
const paperStyles = {
  ...formDialogPaperStyles,
  width: 520,
  maxHeight: 'calc(100% - 32px)',
  display: 'flex',
  flexDirection: 'column',
};

const bodyStyles = {
  ...formDialogBodyStyles,
  pb: 2.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  overflowY: 'auto',
};

// The account in a well a shade darker than the dialog, as the bulk delete lists its accounts.
const accountCardStyles = {
  p: 1.5,
  border: '1px solid',
  borderColor: dialogLine,
  borderRadius: '10px',
  backgroundColor: dialogTray,
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
};

const statsStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  borderTop: '1px solid',
  borderColor: dialogLine,
  pt: 1.25,
};

const statStyles = {
  minWidth: 0,
  px: 1,
  '&:first-of-type': { pl: 0 },
  '&:not(:last-of-type)': { borderRight: '1px solid', borderColor: dialogLine },
};

const hintStyles = {
  ...formDialogTextStyles,
  mt: 0.75,
  fontSize: 12.5,
  lineHeight: '18px',
};

const sectionTitleStyles = {
  mb: 1,
  fontSize: 13,
  fontWeight: 500,
  color: 'text.secondary',
};

const optionStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  '&&': { py: 0.75 },
};

// The second step, which takes the focus when it appears; a programmatic focus draws no ring.
const confirmationStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  outline: 'none',
};

// Three columns, checkbox, icon and text, which the label takes up through a subgrid, so that
// what goes under the label lines up with its text without being in it.
const choiceCardStyles = (checked: boolean, disabled: boolean) => ({
  display: 'grid',
  gridTemplateColumns: 'auto auto minmax(0, 1fr)',
  columnGap: 1.25,
  p: 1.25,
  border: '1px solid',
  borderColor: checked ? 'primary.main' : dialogFrame,
  borderRadius: '10px',
  backgroundColor: checked ? alpha(brand[400], 0.06) : 'transparent',
  transition: 'border-color 120ms, background-color 120ms',
  ...(!disabled && !checked && { '&:has(> label:hover)': { borderColor: alpha('#fff', 0.24) } }),
});

const choiceLabelStyles = (disabled: boolean) => ({
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignItems: 'start',
  cursor: disabled ? 'default' : 'pointer',
});

const choiceIconStyles = (checked: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: '8px',
  color: checked ? brand[300] : gray[400],
  backgroundColor: checked ? alpha(brand[300], 0.12) : alpha('#fff', 0.05),
});

const warningLineStyles = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 0.75,
  mt: 0.5,
  fontSize: 12.5,
  lineHeight: '18px',
  color: orange[300],
  '& svg': { flexShrink: 0, mt: '2px' },
};

const teamListStyles = {
  m: 0,
  mt: 1,
  p: 0,
  listStyle: 'none',
  maxHeight: 176,
  overflowY: 'auto',
  borderTop: '1px solid',
  borderColor: dialogLine,
};

const teamRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  minHeight: 32,
  '&:not(:last-of-type)': { borderBottom: '1px solid', borderColor: dialogLine },
};

const outcomeStyles = {
  flexShrink: 0,
  minWidth: 112,
  justifyContent: 'flex-end',
  textAlign: 'right' as const,
  fontSize: 12.5,
  fontWeight: 500,
};

const bulletListStyles = {
  m: 0,
  pl: 2.25,
  fontSize: 13.5,
  lineHeight: '22px',
  color: 'text.secondary',
};

const panelTextStyles = {
  fontSize: 13.5,
  lineHeight: '21px',
  color: 'text.secondary',
};

export default DeleteUserDialog;
